import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { formatEUR, formatDate } from '../../lib/format';
import { RecordPaymentDialog } from '../../components/collection/RecordPaymentDialog';
import { InvoiceModal } from '../../components/collection/InvoiceModal';
import {
  Banner,
  Modal,
  StatusPill,
  labelCls,
  thCls,
  thNum,
  tdCls,
  tdNum,
  apiMessage,
  cmd,
  useCollectionRole,
  type Cust,
  type Exposure,
  type Inv,
} from '../../components/collection/collectionShared';

/**
 * A customer's account (prototype's "Compte client"): the statement with what is still at the
 * bank, the exposure broken down, why orders are blocked, and the collection actions — record a
 * payment, remind now, a payment promise (30 days at most), pause / resume the letters, and the
 * owner's manual block.
 */
interface Account {
  today: string;
  customer: Cust & {
    limit: number | null;
    status: string;
    pause: { reason: string; until: string | null; by: string | null } | null;
    promise: { date: string; note: string | null; by: string | null } | null;
    manualBlock: { reason: string; by: string | null; at: string | null } | null;
  };
  exposure: Exposure;
  statement: {
    rows: Array<{
      date: string;
      label: string;
      debit: number;
      credit: number;
      pending: number;
      ref: string;
      balance: number;
    }>;
    balance: number;
  };
  invoices: Inv[];
  history: Array<{
    created_at: string;
    action: string;
    detail: string;
    by: string | null;
    system: boolean;
  }>;
}
type Dlg = null | 'pay' | 'promise' | 'pause' | 'block' | 'mandate';

export default function CollectionAccount() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const { owner } = useCollectionRole();
  const [a, setA] = useState<Account | null>(null);
  const [dlg, setDlg] = useState<Dlg>(null);
  const [inv, setInv] = useState<string | null>(null);

  const load = useCallback(() => {
    apiClient
      .get<Account>(`/collection/customers/${id}`)
      .then((r) => setA(r.data))
      .catch((e) => showApiError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      push({ tone: 'success', message: ok });
      setDlg(null);
      load();
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
      throw e;
    }
  };

  if (!a) return <PageHeader title={t('collection.account.title')} />;
  const c = a.customer;
  const e = a.exposure;
  const account = c.type === 'B2B_ACCOUNT';
  const kv = (label: string, value: string, strong?: boolean) => (
    <>
      <span className={strong ? 'border-t pt-2 font-semibold' : 'text-muted-foreground'}>
        {label}
      </span>
      <b className={strong ? 'border-t pt-2 text-right tabular-nums' : 'text-right tabular-nums'}>
        {value}
      </b>
    </>
  );

  return (
    <div>
      <Link
        to="/collection/accounts"
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> {t('collection.accounts.title')}
      </Link>
      <PageHeader
        title={c.name}
        description={[
          c.code,
          t(`collection.type.${c.type}`),
          account
            ? `${c.terms ?? ''}${c.mode ? ` ${t('collection.account.by')} ${t(`collection.mode.${c.mode}`)}` : ''}`
            : null,
          c.sepa ? t('collection.account.mandateShort', { rum: c.sepa.rum }) : null,
          c.email,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      {e.blocked && (
        <Banner tone="bad">
          {t('collection.account.blocked', { reasons: e.reasons.join(' ; ') })}
        </Banner>
      )}
      {c.promise && (
        <Banner tone="info">
          {t('collection.account.promiseBanner', {
            date: formatDate(c.promise.date),
            note: c.promise.note ? ` : ${c.promise.note}` : '',
            by: c.promise.by ?? '',
          })}
        </Banner>
      )}
      {c.pause && (
        <Banner tone="info">
          {t('collection.account.pauseBanner', {
            reason: c.pause.reason,
            until: c.pause.until
              ? t('collection.account.until', { date: formatDate(c.pause.until) })
              : '',
          })}
        </Banner>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="grid gap-4">
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-sm font-semibold">{t('collection.account.statement')}</h2>
              <span className="text-sm text-muted-foreground">
                {t('collection.account.balance', { amount: formatEUR(a.statement.balance) })}
              </span>
            </div>
            <div className="max-h-[480px] overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className={thCls}>{t('collection.col.date')}</th>
                    <th className={thCls}>{t('collection.col.operation')}</th>
                    <th className={thNum}>{t('collection.col.debit')}</th>
                    <th className={thNum}>{t('collection.col.credit')}</th>
                    <th className={thNum}>{t('collection.col.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {a.statement.rows.map((r, n) => (
                    <tr key={n} className="border-b last:border-b-0">
                      <td className={tdCls}>{formatDate(r.date)}</td>
                      <td className={tdCls}>
                        {r.label
                          .replace(/\b(TRANSFER|LCR|SEPA|CHECK|CARD|CASH)\b/, (m) =>
                            t(`collection.mode.${m}`)
                          )
                          .replace(
                            /· (RECU|EMISE|REMISE|ECHUE|PROGRAMME|PRESENTE|PAYE|REJETE)$/,
                            (_m, s) => `· ${t(`collection.state.${s}`)}`
                          )}
                        {r.pending > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {t('collection.account.pendingRow', { amount: formatEUR(r.pending) })}
                          </div>
                        )}
                      </td>
                      <td className={tdNum}>{r.debit ? formatEUR(r.debit) : ''}</td>
                      <td className={tdNum}>{r.credit ? formatEUR(r.credit) : ''}</td>
                      <td className={tdNum}>{formatEUR(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {a.invoices.length > 0 && (
            <div className="rounded-lg border bg-card shadow-sm">
              <div className="border-b px-4 py-3">
                <h2 className="text-sm font-semibold">{t('collection.account.open')}</h2>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {a.invoices.map((i) => (
                    <tr
                      key={i.id}
                      className="cursor-pointer border-b last:border-b-0 hover:bg-muted/50"
                      onClick={() => setInv(i.id)}
                    >
                      <td className={tdCls}>
                        <span className="font-mono text-xs">{i.number}</span>
                        <div className="text-xs text-muted-foreground">
                          {t('collection.overview.dueOn', { date: formatDate(i.dueDate) })}
                        </div>
                      </td>
                      <td className={tdNum}>{formatEUR(i.s.remaining)}</td>
                      <td className={tdCls}>
                        <StatusPill s={i.s} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid gap-4">
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="border-b px-4 py-3">
              <h2 className="text-sm font-semibold">{t('collection.account.exposure')}</h2>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 px-4 py-3 text-sm">
              {kv(t('collection.account.undelivered'), formatEUR(e.undelivered))}
              {kv(t('collection.account.notInvoiced'), formatEUR(e.notInvoiced))}
              {kv(t('collection.account.invoicedUnpaid'), formatEUR(e.invoicedUnpaid))}
              {e.inCollection > 0 &&
                kv(t('collection.account.inCollection'), formatEUR(e.inCollection))}
              {e.advance > 0 && kv(t('collection.account.advance'), `− ${formatEUR(e.advance)}`)}
              {kv(t('collection.account.total'), formatEUR(e.total), true)}
              {account &&
                kv(
                  t('collection.account.limit'),
                  c.limit == null ? t('collection.accounts.unlimited') : formatEUR(c.limit)
                )}
              {account &&
                e.release > 0 &&
                kv(t('collection.account.release'), formatEUR(e.release))}
              {account &&
                e.available != null &&
                kv(t('collection.account.available'), formatEUR(e.available))}
            </div>
            <p className="border-t px-4 py-2 text-xs text-muted-foreground">
              {t('collection.account.exposureNote')}
            </p>
          </div>
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="border-b px-4 py-3">
              <h2 className="text-sm font-semibold">{t('collection.account.actions')}</h2>
            </div>
            <div className="grid gap-2 px-4 py-3">
              <Button onClick={() => setDlg('pay')}>{t('collection.pay.title')}</Button>
              {account && (
                <>
                  <Button
                    variant="outline"
                    onClick={() =>
                      act(
                        () => apiClient.post(`/collection/customers/${id}/remind`, {}, cmd()),
                        t('collection.account.reminded')
                      ).catch(() => undefined)
                    }
                  >
                    {t('collection.account.remind')}
                  </Button>
                  {c.promise ? (
                    <Button
                      variant="outline"
                      onClick={() =>
                        act(
                          () => apiClient.delete(`/collection/customers/${id}/promise`, cmd()),
                          t('collection.account.promiseCleared')
                        ).catch(() => undefined)
                      }
                    >
                      {t('collection.account.clearPromise')}
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => setDlg('promise')}>
                      {t('collection.account.promise')}
                    </Button>
                  )}
                  {c.pause ? (
                    <Button
                      variant="outline"
                      onClick={() =>
                        act(
                          () => apiClient.delete(`/collection/customers/${id}/pause`, cmd()),
                          t('collection.account.resumed')
                        ).catch(() => undefined)
                      }
                    >
                      {t('collection.account.resume')}
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => setDlg('pause')}>
                      {t('collection.account.pause')}
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setDlg('mandate')}>
                    {c.sepa
                      ? t('collection.account.mandateEdit')
                      : t('collection.account.mandateAdd')}
                  </Button>
                  {owner &&
                    (c.manualBlock ? (
                      <Button
                        variant="outline"
                        onClick={() =>
                          act(
                            () => apiClient.delete(`/collection/customers/${id}/block`, cmd()),
                            t('collection.account.unblocked')
                          ).catch(() => undefined)
                        }
                      >
                        {t('collection.account.unblock')}
                      </Button>
                    ) : (
                      <Button variant="destructive" onClick={() => setDlg('block')}>
                        {t('collection.account.block')}
                      </Button>
                    ))}
                </>
              )}
            </div>
          </div>
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="border-b px-4 py-3">
              <h2 className="text-sm font-semibold">{t('collection.account.history')}</h2>
            </div>
            <ol className="grid gap-2.5 px-4 py-3 text-sm">
              {a.history.length === 0 && (
                <li className="text-muted-foreground">{t('collection.account.noHistory')}</li>
              )}
              {a.history.map((h, n) => (
                <li key={n} className="grid grid-cols-[88px_1fr] gap-2">
                  <time className="tabular-nums text-muted-foreground">
                    {formatDate(h.created_at)}
                  </time>
                  <div>
                    {h.detail}
                    <div className="text-xs text-muted-foreground">
                      {h.system ? t('collection.common.system') : h.by}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {dlg === 'pay' && (
        <RecordPaymentDialog customerId={id} onClose={() => setDlg(null)} onDone={load} />
      )}
      {inv && <InvoiceModal invoiceId={inv} onClose={() => setInv(null)} />}
      {dlg === 'promise' && (
        <FormDialog
          title={t('collection.account.promiseTitle')}
          lede={t('collection.account.promiseLede')}
          fields={[
            { key: 'date', label: t('collection.account.promiseDate'), type: 'date', min: a.today },
            { key: 'note', label: t('collection.account.note'), type: 'text' },
          ]}
          onClose={() => setDlg(null)}
          onSubmit={(v) =>
            act(
              () => apiClient.post(`/collection/customers/${id}/promise`, v, cmd()),
              t('collection.account.promiseSaved')
            )
          }
        />
      )}
      {dlg === 'pause' && (
        <FormDialog
          title={t('collection.account.pauseTitle')}
          lede={t('collection.account.pauseLede')}
          fields={[
            { key: 'reason', label: t('collection.col.reason'), type: 'area', min10: true },
            { key: 'until', label: t('collection.account.pauseUntil'), type: 'date', min: a.today },
          ]}
          onClose={() => setDlg(null)}
          onSubmit={(v) =>
            act(
              () => apiClient.post(`/collection/customers/${id}/pause`, v, cmd()),
              t('collection.account.paused')
            )
          }
        />
      )}
      {dlg === 'block' && (
        <FormDialog
          title={t('collection.account.blockTitle')}
          lede={t('collection.account.blockLede')}
          danger
          fields={[{ key: 'reason', label: t('collection.col.reason'), type: 'area', min10: true }]}
          onClose={() => setDlg(null)}
          onSubmit={(v) =>
            act(
              () => apiClient.post(`/collection/customers/${id}/block`, v, cmd()),
              t('collection.account.blockedDone')
            )
          }
        />
      )}
      {dlg === 'mandate' && (
        <FormDialog
          title={t('collection.account.mandateTitle')}
          lede={t('collection.account.mandateLede')}
          initial={{ rum: c.sepa?.rum ?? '', signedAt: c.sepa?.signedAt ?? '' }}
          fields={[
            { key: 'rum', label: t('collection.account.rum'), type: 'text' },
            {
              key: 'signedAt',
              label: t('collection.account.signedAt'),
              type: 'date',
              max: a.today,
            },
          ]}
          onClose={() => setDlg(null)}
          onSubmit={(v) =>
            act(
              () => apiClient.put(`/collection/customers/${id}/mandate`, v, cmd()),
              t('collection.account.mandateSaved')
            )
          }
        />
      )}
    </div>
  );
}

interface Field {
  key: string;
  label: string;
  type: 'text' | 'date' | 'area';
  min?: string;
  max?: string;
  min10?: boolean;
}
function FormDialog({
  title,
  lede,
  fields,
  initial,
  danger,
  onClose,
  onSubmit,
}: {
  title: string;
  lede?: string;
  fields: Field[];
  initial?: Record<string, string>;
  danger?: boolean;
  onClose: () => void;
  onSubmit: (v: Record<string, string>) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState<Record<string, string>>(initial ?? {});
  const [busy, setBusy] = useState(false);
  const ok = fields.every((f) => !f.min10 || (v[f.key] ?? '').trim().length >= 10);
  return (
    <Modal
      title={title}
      lede={lede}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('collection.common.cancel')}
          </Button>
          <Button
            variant={danger ? 'destructive' : 'default'}
            disabled={busy || !ok}
            onClick={() => {
              setBusy(true);
              onSubmit(v)
                .catch(() => undefined)
                .finally(() => setBusy(false));
            }}
          >
            {t('collection.common.save')}
          </Button>
        </>
      }
    >
      {fields.map((f) => (
        <label key={f.key} className="block">
          <span className={labelCls}>{f.label}</span>
          {f.type === 'area' ? (
            <Textarea
              rows={3}
              value={v[f.key] ?? ''}
              onChange={(e) => setV({ ...v, [f.key]: e.target.value })}
            />
          ) : (
            <Input
              type={f.type}
              min={f.min}
              max={f.max}
              value={v[f.key] ?? ''}
              onChange={(e) => setV({ ...v, [f.key]: e.target.value })}
            />
          )}
          {f.min10 && (
            <span className="mt-1 block text-xs text-muted-foreground">
              {t('collection.common.min10')}
            </span>
          )}
        </label>
      ))}
    </Modal>
  );
}
